import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { TypedMemo } from '../../../common/types';
import type { CampaignResultDay } from '../outreachTypes';
import { barPercent, dailyChartTop, dayLabelStep } from './dailyChartScale';
import { resultsMessages as messages } from './resultsMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for Outreach; each part uses its own slice of it */
import shared from '../Outreach.module.scss';
import card from '../live/Campaign.module.scss';
import styles from './Results.module.scss';
/* eslint-enable css-modules/no-unused-class */

/**
 * "Day by day" (design outreach-v2 t6): one bar per day. The outline is the pitches of the day,
 * filled from the bottom by the listens (pink) and the opens (violet) of those pitches. A green dot
 * marks a day with an add. The title of a bar names every number of the day.
 */
const DailyChartComponent: React.FC<{ days: CampaignResultDay[] }> = ({ days }) => {
  const intl = useIntl();
  const top = dailyChartTop(days);
  const step = dayLabelStep(days.length);
  const bars = useMemo(
    () =>
      days.map((day, index) => {
        const label = intl.formatDate(new Date(`${day.day}T12:00:00Z`), {
          month: 'short',
          day: 'numeric',
          timeZone: 'UTC',
        });
        const title = intl.formatMessage(messages.dailyBar, { ...day, day: label });
        const openedOnly = Math.max(0, day.opened - day.listened);
        return (
          <div key={day.day} className={styles.dayCol} title={title} aria-label={title} role="img">
            <div className={styles.dayBar} style={{ height: `${barPercent(day.pitched, top)}%` }}>
              <i
                className={styles.dayListened}
                style={{ height: `${barPercent(day.listened, Math.max(1, day.pitched))}%` }}
              />
              <i
                className={styles.dayOpened}
                style={{ height: `${barPercent(openedOnly, Math.max(1, day.pitched))}%` }}
              />
            </div>
            {day.added > 0 && <span className={styles.dayAdd} aria-hidden />}
            <span className={clsx(styles.dayLabel, index % step !== 0 && styles.dayLabelHidden)}>{label}</span>
          </div>
        );
      }),
    [days, intl, step, top]
  );
  return (
    <section className={card.card} data-testid="results-daily">
      <div className={styles.dailyHead}>
        <div>
          <h3 className={card.cardTitle}>{intl.formatMessage(messages.dailyTitle)}</h3>
          <p className={card.cardIntro}>{intl.formatMessage(messages.dailyIntro)}</p>
        </div>
        <div className={styles.legend}>
          <span className={styles.legendItem}>
            <i className={clsx(styles.swatch, styles.swatchPitched)} />
            {intl.formatMessage(messages.dailyPitched)}
          </span>
          <span className={styles.legendItem}>
            <i className={clsx(styles.swatch, styles.swatchOpened)} />
            {intl.formatMessage(messages.dailyOpened)}
          </span>
          <span className={styles.legendItem}>
            <i className={clsx(styles.swatch, styles.swatchListened)} />
            {intl.formatMessage(messages.dailyListened)}
          </span>
          <span className={styles.legendItem}>
            <i className={clsx(styles.swatch, styles.swatchAdded)} />
            {intl.formatMessage(messages.dailyAdded)}
          </span>
        </div>
      </div>
      {days.length === 0 ? (
        <p className={`${shared.small} mb-0`}>{intl.formatMessage(messages.dailyEmpty)}</p>
      ) : (
        <div className={styles.dailyPlot}>
          <div className={styles.dailyAxis} aria-hidden>
            <span>{intl.formatNumber(top)}</span>
            <span>0</span>
          </div>
          <div className={styles.dailyBars}>{bars}</div>
        </div>
      )}
    </section>
  );
};

export const DailyChart = TypedMemo(DailyChartComponent);
